/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Appointments table and pagination for ViewAppointments.
 * @module pages/viewAppointments/ViewAppointmentsTablePanel
 */

import PageSelector from "../../components/PageSelector";
import ResultsDropdown from "../../components/ResultsDropdown";
import styles from "../../styles/ViewAppointments.module.css";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/BusinessEvents/CheckmarkIcon.svg";
import { ReactComponent as WarningIcon } from "../../assets/icons/BusinessEvents/WarningIcon.svg";
import { ReactComponent as LikeIcon } from "../../assets/icons/BusinessEvents/LikeIcon.svg";
import { ReactComponent as MoreIcon } from "../../assets/icons/Chat/MoreIcon.svg";
import { getAppointmentStatusTranslationKey } from "../../utils/appointments/appointmentStatusDisplay.js";

const getEmptyStateMessage = ({
  t,
  userData,
  selectedBuyerCompany,
  selectedBuyerExecutive,
  selectedRequirement,
  debouncedSearch,
}) => {
  if (userData?.tipoDeUsuario !== "5") {
    return debouncedSearch
      ? t("pages.viewAppointments.noResults")
      : t("pages.viewAppointments.noRequests");
  }
  if (!selectedBuyerCompany) {
    return t("pages.viewAppointments.chooseCompany");
  }
  if (!selectedBuyerExecutive) {
    return t("pages.viewAppointments.chooseBuyer");
  }
  if (!selectedRequirement && !debouncedSearch) {
    return t("pages.viewAppointments.noRequests");
  }
  return t("pages.viewAppointments.noResults");
};

const ViewAppointmentsTablePanel = ({
  t,
  userData,
  filteredRequests,
  selectedBuyerCompany,
  selectedBuyerExecutive,
  selectedRequirement,
  debouncedSearch,
  totalAppointments,
  currentPage,
  resultsLimit,
  getAppointmentStatusStyleKey,
  onActionsDropdownClick,
  onResultsLimitChange,
  onPageChange,
}) => {
  const showEmptyState =
    filteredRequests.length === 0 ||
    (userData?.tipoDeUsuario === "5" &&
      (!selectedBuyerCompany || !selectedBuyerExecutive));

  return (
    <div className={styles.leftPanel}>
      <div className={styles.requirementsStatusSection}>
        <div className={styles.requirementsStatusRow}>
          <div className={styles.requirementsStatusContainer}>
            <div className={styles.requirementStatusItem}>
              <CheckmarkIcon className={styles.statusIcon} />
              <span className={styles.statusText}>
                {t("pages.viewAppointments.fulfilledRequirements")}
              </span>
            </div>
            <div className={styles.requirementStatusItem}>
              <WarningIcon className={styles.statusIcon} />
              <span className={styles.statusText}>
                {t("pages.viewAppointments.missingRequirements")}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className={styles.tableSection}>
        <div className={styles.tableContainer}>
          {showEmptyState ? (
            <div className={styles.emptyState}>
              <p className={styles.emptyStateMessage}>
                {getEmptyStateMessage({
                  t,
                  userData,
                  selectedBuyerCompany,
                  selectedBuyerExecutive,
                  selectedRequirement,
                  debouncedSearch,
                })}
              </p>
            </div>
          ) : (
            <table className={styles.appointmentsTable}>
              <thead>
                <tr>
                  <th>{t("pages.viewAppointments.memberName")}</th>
                  <th>{t("pages.viewAppointments.status")}</th>
                  <th>{t("pages.viewAppointments.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((request) => (
                  <tr key={request._id} className={styles.tableRow}>
                    <td className={styles.titleCell}>
                      <div className={styles.memberNameContainer}>
                        {request.fulfilledRequirements ? (
                          <CheckmarkIcon className={styles.requirementIcon} />
                        ) : (
                          <WarningIcon className={styles.requirementIcon} />
                        )}
                        <div className={styles.memberInfo}>
                          <div className={styles.appointmentTitle}>
                            {request.memberName}
                          </div>
                          <div className={styles.appointmentDescription}>
                            {request.companyName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className={styles.statusCell}>
                      <div className={styles.statusContainer}>
                        {request.meGusta === true && (
                          <LikeIcon className={styles.likeIcon} />
                        )}
                        <span
                          className={`${styles.statusBadge} ${styles[getAppointmentStatusStyleKey(request)]}`}
                        >
                          {t(getAppointmentStatusTranslationKey(request))}
                        </span>
                      </div>
                    </td>
                    <td className={styles.actionsCell}>
                      <div className={styles.actionsDropdownContainer}>
                        <button
                          className={styles.actionsDropdownButton}
                          data-request-id={request._id}
                          onClick={onActionsDropdownClick}
                        >
                          <MoreIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        {totalAppointments > 0 && (
          <div className={styles.paginationFooter}>
            <ResultsDropdown onResultsLimitChange={onResultsLimitChange} />
            <PageSelector
              currentPage={currentPage}
              totalPages={Math.ceil(totalAppointments / resultsLimit) || 1}
              onPageChange={onPageChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ViewAppointmentsTablePanel;
